import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";
import "./mobile-improvements.css";
import WelcomePage from "./components/WelcomePage";
import LoginPage from "./components/LoginPage";
import RegisterPage from "./components/RegisterPage";
import ForgotPasswordPage from "./components/ForgotPasswordPage";
import ResetPasswordPage from "./components/ResetPasswordPage";
import EmailVerificationPage from "./components/EmailVerificationPage";
import MasterDashboardPage from "./components/MasterDashboardPage";
import Dashboard from "./components/Dashboard";
import StaffWorkspace from "./components/StaffWorkspace";
import RequestForm from "./components/RequestForm";
import RequestDetail from "./components/RequestDetail";
import DepartmentRegistration from "./components/DepartmentRegistration";
import DepartmentPortal from "./components/DepartmentPortal";
import TermsAndPrivacy from "./components/TermsAndPrivacy";
import { Toaster } from "./components/ui/sonner";
import { shouldShowMarketingPage, shouldShowDepartmentPortal } from "./utils/subdomainUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Set up axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Auth Context
export const AuthContext = React.createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, API }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  return user ? children : <Navigate to="/login" />;
};

// Public Route Component (redirect if already logged in)
const PublicRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  return !user ? children : <Navigate to="/dashboard" />;
};

// Home Route Component (handles subdomain detection)
const HomeRoute = ({ children }) => {
  // Check if this is a department subdomain
  if (shouldShowDepartmentPortal()) {
    return <DepartmentPortal />;
  }
  
  // Show marketing page for main domain
  if (shouldShowMarketingPage()) {
    return children;
  }
  
  // Fallback to marketing page
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
        <BrowserRouter>
          <Routes>
            <Route 
              path="/" 
              element={
                <HomeRoute>
                  <WelcomePage />
                </HomeRoute>
              } 
            />
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/register" 
              element={
                <PublicRoute>
                  <RegisterPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/verify-email" 
              element={
                <PublicRoute>
                  <EmailVerificationPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/forgot-password" 
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/reset-password/:token" 
              element={
                <PublicRoute>
                  <ResetPasswordPage />
                </PublicRoute>
              } 
            />
            <Route 
              path="/terms-and-privacy" 
              element={<TermsAndPrivacy />} 
            />
            <Route 
              path="/master" 
              element={<MasterDashboardPage />} 
            />
            <Route 
              path="/register-department" 
              element={
                <DepartmentRegistration />
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/staff-workspace" 
              element={
                <ProtectedRoute>
                  <StaffWorkspace />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/request/new" 
              element={
                <ProtectedRoute>
                  <RequestForm />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/request/:id" 
              element={
                <ProtectedRoute>
                  <RequestDetail />
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;